import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useHaptics } from '../../utils/haptics'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'
import StatusIndicator from '../common/StatusIndicator'
import { useMoney } from '../../hooks/usePublicSettings'
import { bookingApi, moneyRange } from './bookingApi'

// Icons
function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

function DollarSignIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  )
}

function ClockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="10"/>
      <path d="M12 6v6l4 2"/>
    </svg>
  )
}

function ToolIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14.7 6.3a4 4 0 1 0-5.4 5.4l-7 7 2 2 7-7a4 4 0 0 0 5.4-5.4z"/>
    </svg>
  )
}

function UsersIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  )
}

function SearchIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="8"/>
      <path d="M21 21l-4.35-4.35"/>
    </svg>
  )
}

function StarIcon({ filled = false, ...props }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
    </svg>
  )
}

function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

export default function ServiceCards() {
  const { serviceType } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const haptics = useHaptics()
  const money = useMoney()
  const [tradeInfo, setTradeInfo] = useState(null)   // { pros, hourlyRate }
  
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [professionals, setProfessionals] = useState([])
  const [loadingProfessionals, setLoadingProfessionals] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredServices, setFilteredServices] = useState([])

  // Get any context passed from navigation
  const navigationState = location.state || {}

  useEffect(() => {
    loadServiceCards()
    loadProfessionals()
  }, [serviceType])

  useEffect(() => {
    // Filter services based on search query
    if (searchQuery.trim() === '') {
      setFilteredServices(services)
    } else {
      const filtered = services.filter(service =>
        service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setFilteredServices(filtered)
    }
  }, [services, searchQuery])

  // Services = repair methods for this trade; counts and prices from professionals' capabilities and rate cards
  const loadServiceCards = async () => {
    setLoading(true)
    try {
      const data = await bookingApi.services(serviceType)
      setTradeInfo({ pros: data.pros, hourlyRate: data.hourlyRate })
      setServices((data.services || []).map(m => ({
        id: m.id,
        method: m.id,
        title: m.title,
        description: m.description || '',
        unit: m.unit,
        minQuantity: m.minQuantity,
        unitPrice: m.unitPrice,
        fixedPrice: m.fixedPrice,
        availablePros: m.pros,
        pricedBy: m.pricedBy,
        difficulty: m.safety === 'diy_ok' ? 'DIY Possible' : m.safety === 'pro_only' || m.safety === 'emergency' ? 'Professional Required' : 'Standard',
        icon: getServiceTypeIcon(serviceType)
      })))
    } catch (error) {
      console.error('Error loading service cards:', error)
      setServices([])
      setTradeInfo(null)
    } finally {
      setLoading(false)
    }
  }

  // Price range from pros' rate cards, formatted in the market currency (null when nobody priced it)
  const priceText = service => service.unitPrice
    ? `${moneyRange(money, service.unitPrice)} per ${service.unitPrice.unit}`
    : service.fixedPrice ? moneyRange(money, service.fixedPrice) : null

  const loadProfessionals = async () => {
    setLoadingProfessionals(true)
    try {
      const data = await bookingApi.professionals({ trade: serviceType, limit: 10 })
      setProfessionals(data.professionals || [])   // ranked by real rating on the server
    } catch (error) {
      console.error('Error loading professionals:', error)
      setProfessionals([])
    } finally {
      setLoadingProfessionals(false)
    }
  }

  const handleRefresh = async () => {
    haptics.light()
    await loadServiceCards()
    await loadProfessionals()
  }

  const requiresParts = (serviceTitle) => {
    // Services that typically require parts
    const partsBasedServices = [
      'replace', 'install', 'new', 'add', 'upgrade'
    ]
    return partsBasedServices.some(keyword => 
      serviceTitle.toLowerCase().includes(keyword)
    )
  }

  const handleProfessionalAction = (professional, service = null) => {
    haptics.light()
    
    // If service is provided, check if it requires parts
    if (service && requiresParts(service.title)) {
      // Navigate to service request for RFQ
      navigate(`/service-request`, {
        state: {
          serviceDetails: { ...service, estimatedPrice: priceText(service) },
          professionalId: professional.id,
          fromServiceCard: true,
          requiresQuote: true
        }
      })
    } else {
      // Direct booking for labor-only services
      navigate(`/booking/datetime/${professional.id}`, { state: { trade: serviceType, method: service?.method } })
    }
  }

  const getRequiredExpertise = (serviceTitle, serviceType) => {
    // Map services to required professional expertise
    const expertiseMap = {
      // Plumbing services
      'Replace Water Heater': ['plumbing'],
      'Fix Leaky Faucet': ['plumbing'],
      'Unclog Drain': ['plumbing'],
      'Install New Toilet': ['plumbing'],
      
      // Electrical services  
      'Add Electrical Outlet': ['electrical'],
      'Fix Circuit Breaker': ['electrical'],
      'Install Ceiling Fan': ['electrical'],
      'Electrical Wiring': ['electrical'],
      
      // HVAC services
      'Install AC Unit': ['hvac'],
      'AC Maintenance': ['hvac'],
      'Heating Repair': ['hvac'],
      
      // Carpentry services
      'Install Shelves': ['carpentry'],
      'Build Custom Cabinet': ['carpentry'],
      'Door Installation': ['carpentry'],
      
      // Painting services
      'Paint Room Interior': ['painting'],
      'Exterior Painting': ['painting'],
      
      // General maintenance
      'General Repair': ['general', 'handyman']
    }
    
    // Return specific expertise or fallback to service type
    return expertiseMap[serviceTitle] || [serviceType] || ['general']
  }

  const handleServiceSelect = (service) => {
    haptics.light()
    // Navigate to service request form for proper RFQ flow with detailed information gathering
    navigate(`/service-request`, { 
      state: { 
        serviceDetails: { ...service, estimatedPrice: priceText(service) },
        serviceType: serviceType,
        preSelectedService: service.title,
        fromServiceCard: true,
        // Service-specific requirements
        requiresPhotos: true,
        requiresEquipmentDetails: true,
        requiresFaultDescription: true,
        requiresCostBreakdown: true,
        // Professional matching criteria
        requiredExpertise: getRequiredExpertise(service.title, serviceType)
      } 
    })
  }

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'bg-red-100 text-red-700'
      case 'moderate': return 'bg-yellow-100 text-yellow-700'
      case 'low': return 'bg-green-100 text-green-700'
      default: return 'bg-slate-100 text-slate-700'
    }
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'DIY Possible': return 'text-green-600'
      case 'Standard': return 'text-blue-600'
      case 'Professional Required': return 'text-orange-600'
      default: return 'text-slate-600'
    }
  }

  const getServiceTypeTitle = (type) => {
    const titles = {
      plumbing: 'Plumbing Services',
      electrical: 'Electrical Services', 
      painting: 'Painting Services',
      carpentry: 'Carpentry Services',
      consultant: 'Consultation Services'
    }
    return titles[type] || 'Services'
  }

  const getServiceTypeIcon = (type) => {
    const icons = {
      plumbing: '🔧',
      electrical: '⚡',
      painting: '🎨',
      carpentry: '🔨',
      consultant: '👨‍💼'
    }
    return icons[type] || '🛠️'
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation"
          >
            <ArrowLeftIcon />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getServiceTypeIcon(serviceType)}</span>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">{getServiceTypeTitle(serviceType)}</h1>
              <p className="text-sm text-slate-500">Choose the service you need</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-6 pb-20">
        {/* Search Bar */}
        <Card>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services..."
              className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
          </div>
        </Card>

        {/* Hot Service Buttons Carousel - 15% of screen */}
        <Card>
          <h3 className="font-semibold text-slate-900 mb-3">Quick Services</h3>
          {!loading && services.length === 0 && (
            <p className="text-sm text-slate-500">No listed services for this trade yet — browse the professionals below.</p>
          )}
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {(searchQuery ? filteredServices : services).map((service) => (
              <button
                key={service.id}
                onClick={() => handleServiceSelect(service)}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-full text-sm font-medium transition-colors touch-manipulation"
              >
                <span className="text-lg">{service.icon}</span>
                <span className="whitespace-nowrap">{service.title}</span>
                {priceText(service) && <span className="text-xs text-slate-500 whitespace-nowrap">{priceText(service)}</span>}
                {requiresParts(service.title) && (
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">RFQ</span>
                )}
              </button>
            ))}
          </div>
        </Card>

        {/* Browse Professionals Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Browse {getServiceTypeTitle(serviceType)}
            </h2>
            <span className="text-sm text-slate-500">
              {tradeInfo?.pros ?? professionals.length} available • Top rated first
            </span>
          </div>

          {loadingProfessionals ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="mb-4">
                <div className="animate-pulse">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-slate-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                      <div className="h-3 bg-slate-200 rounded w-full"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : professionals.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">👨‍🔧</span>
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">No professionals found</h3>
                <p className="text-slate-600 text-sm">Try a different service category or check back later.</p>
              </div>
            </Card>
          ) : (
            professionals.map((professional) => (
              <Card key={professional.id} className="mb-4">
                <div className="flex items-start gap-4">
                  {/* Professional Avatar */}
                  <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-semibold text-slate-600">
                      {(professional.name || '').split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>

                  {/* Professional Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">{professional.name}</h3>
                          <StatusIndicator 
                            status={professional.onlineStatus} 
                            size="xs"
                            showText={false}
                            showResponseTime={false}
                          />
                          {professional.onlineStatus === 'online' && (
                            <span className="text-xs text-slate-500">
                              {professional.responseTime}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{professional.company}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                        <StarIcon filled className="text-yellow-400" />
                        <span className="text-sm font-medium text-slate-900">
                          {professional.rating != null ? Number(professional.rating).toFixed(1) : '—'}
                        </span>
                        <span className="text-sm text-slate-500">
                          ({professional.reviewCount})
                        </span>
                      </div>
                    </div>

                    {/* Professional Details */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span>{professional.hourlyRate ? `${moneyRange(money, professional.hourlyRate)}/hr` : '—'}</span>
                        {professional.location && <span>{professional.location}</span>}
                        <span>{professional.completedJobs} jobs</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {professional.verified && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircleIcon className="w-3 h-3" />
                            <span className="text-xs font-medium">Verified</span>
                          </div>
                        )}
                        {professional.emergencyAvailable && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-md">
                            24/7
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleProfessionalAction(professional)}
                        className="flex-1 py-2 px-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 touch-manipulation text-sm"
                      >
                        Book Now
                      </button>
                      <button
                        onClick={() => {
                          haptics.light()
                          navigate(`/professionals/${professional.id}`)
                        }}
                        className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 touch-manipulation text-sm"
                      >
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
